import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';

import { replaceJianyingTemplateSlotBindings } from '../../../services/jianyingTemplateService.js';
import {
  applyQuickBindings,
  buildBindingOwnerMap,
  buildBindingBatchPayload,
  buildBindingImageSections,
  buildBindingSlotPathMap,
  buildBindingTargetSections,
  getBindingDraft,
  getBoundSlotsForImage,
  getJianyingBindingImageSlots,
  isBindingTemplateDirty,
  setBindingGroupOpen,
  toggleBindingTarget,
  updateBindingDraft,
} from './JianyingTemplateSlotBindingModel.js';

const TARGET_META = {
  text: { icon: 'bi-fonts', label: '文字' },
  audio: { icon: 'bi-music-note-beamed', label: '音频' },
};

const countSlots = (nodes) => nodes.reduce((total, node) => (
  total + (node.type === 'slot' ? 1 : countSlots(node.children))
), 0);

const getErrorMessage = (error, fallback) => (
  error.response?.data?.message || error.message || fallback
);

const BindingTarget = ({
  busy,
  draftIds,
  onToggle,
  owner,
  ownerPath,
  selectedImageId,
  slot,
}) => {
  const meta = TARGET_META[slot.kind];
  const selected = draftIds.has(String(slot.id));
  const occupied = owner && String(owner.id) !== String(selectedImageId);
  const resolvedOwnerPath = ownerPath || owner?.name || '其他图片';
  const inputId = `jianying-binding-target-${slot.id}`;

  return (
    <label
      className={`jianying-binding-target is-${slot.kind} ${selected ? 'is-selected' : ''} ${occupied ? 'is-occupied' : ''}`}
      htmlFor={inputId}
      title={occupied ? `已绑定到“${resolvedOwnerPath}”` : undefined}
    >
      <input
        id={inputId}
        type="checkbox"
        checked={selected}
        disabled={busy || occupied}
        onChange={() => onToggle(slot)}
      />
      <i className={`bi ${meta.icon}`} aria-hidden="true" />
      <span className="jianying-binding-target__identity">
        <strong>{slot.name || '未命名槽位'}</strong>
        {slot.original_content ? <small>{slot.original_content}</small> : null}
      </span>
      <span className="jianying-binding-target__state">
        <em>{meta.label}</em>
        {occupied ? <small>已绑定到 {resolvedOwnerPath}</small> : null}
      </span>
    </label>
  );
};

const BindingImage = ({
  active, boundSlots, busy, image, onSelect, slotPaths,
}) => {
  return (
    <button
      type="button"
      className={`jianying-binding-image ${active ? 'is-active' : ''}`}
      aria-pressed={active}
      disabled={busy}
      onClick={() => onSelect(image.id)}
    >
      <span className="jianying-binding-image__identity">
        <strong>{image.name || '未命名图片槽位'}</strong>
        {boundSlots.length ? (
          <span className="jianying-binding-image__bindings">
            {boundSlots.map((slot) => (
              <em key={slot.id}>
                {TARGET_META[slot.kind]?.label} · {slotPaths.get(String(slot.id))}
              </em>
            ))}
          </span>
        ) : <small>尚未绑定</small>}
      </span>
      <i className="bi bi-chevron-right" aria-hidden="true" />
    </button>
  );
};

const BindingGroup = ({
  busy,
  collapsedGroupIds,
  countLabel,
  node,
  onGroupToggle,
  renderSlot,
  searchActive,
}) => {
  if (node.type === 'slot') {
    return renderSlot(node.slot);
  }

  const toggleGroup = () => {
    if (!busy && !searchActive) {
      onGroupToggle(node.key, collapsedGroupIds.has(node.key));
    }
  };

  return (
    <details
      className="jianying-binding-group"
      open={searchActive || !collapsedGroupIds.has(node.key)}
    >
      <summary aria-disabled={busy} onClick={(event) => {
        event.preventDefault();
        toggleGroup();
      }} onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          toggleGroup();
        }
      }}>
        <span>
          <strong>{node.group.name}</strong>
          <small>{countSlots(node.children)} {countLabel}</small>
        </span>
        <i className="bi bi-chevron-down" aria-hidden="true" />
      </summary>
      <div className="jianying-binding-group__children">
        {node.children.map((child) => (
          <BindingGroup
            busy={busy}
            collapsedGroupIds={collapsedGroupIds}
            countLabel={countLabel}
            key={child.key}
            node={child}
            onGroupToggle={onGroupToggle}
            renderSlot={renderSlot}
            searchActive={searchActive}
          />
        ))}
      </div>
    </details>
  );
};

const JianyingTemplateSlotBindingEditor = ({
  onChanged,
  onDirtyChange,
  onSavingChange,
  template,
}) => {
  const [baselineTemplate, setBaselineTemplate] = useState(template);
  const [displayTemplate, setDisplayTemplate] = useState(template);
  const appliedPropRef = useRef(template);
  const imageSlots = useMemo(
    () => getJianyingBindingImageSlots(displayTemplate),
    [displayTemplate],
  );
  const [selectedImageId, setSelectedImageId] = useState(
    () => getJianyingBindingImageSlots(template)[0]?.id || '',
  );
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');
  const [collapsedImageGroupIds, setCollapsedImageGroupIds] = useState(() => new Set());
  const [collapsedTargetGroupIds, setCollapsedTargetGroupIds] = useState(() => new Set());
  const [message, setMessage] = useState('');
  const [messageTone, setMessageTone] = useState('');
  const selectedImage = imageSlots.find(
    (slot) => String(slot.id) === String(selectedImageId),
  ) || null;
  const draft = getBindingDraft(selectedImage, displayTemplate);
  const dirty = isBindingTemplateDirty(baselineTemplate, displayTemplate);
  const imageSections = useMemo(
    () => buildBindingImageSections(displayTemplate),
    [displayTemplate],
  );
  const targetSections = useMemo(
    () => buildBindingTargetSections(displayTemplate, search),
    [displayTemplate, search],
  );
  const owners = useMemo(() => buildBindingOwnerMap(imageSlots), [imageSlots]);
  const slotPaths = useMemo(
    () => buildBindingSlotPathMap(displayTemplate),
    [displayTemplate],
  );
  const groupsById = useMemo(() => new Map(
    (Array.isArray(displayTemplate?.slot_groups) ? displayTemplate.slot_groups : [])
      .map((group) => [String(group.id), group]),
  ), [displayTemplate?.slot_groups]);
  const draftIds = useMemo(() => new Set([
    ...draft.text_slot_ids,
    ...draft.audio_slot_ids,
  ].map(String)), [draft.audio_slot_ids, draft.text_slot_ids]);
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  useEffect(() => {
    onSavingChange?.(busy);
  }, [busy, onSavingChange]);

  useEffect(() => () => {
    onDirtyChange?.(false);
    onSavingChange?.(false);
  }, [onDirtyChange, onSavingChange]);

  useEffect(() => {
    if (template === appliedPropRef.current) return;
    if (busy || dirty) return;
    const nextImages = getJianyingBindingImageSlots(template);
    const nextImageId = nextImages.find(
      (slot) => String(slot.id) === String(selectedImageId),
    )?.id || nextImages[0]?.id || '';
    appliedPropRef.current = template;
    setBaselineTemplate(template);
    setDisplayTemplate(template);
    setSelectedImageId(nextImageId);
  }, [busy, dirty, selectedImageId, template]);

  useEffect(() => {
    if (!dirty && !busy) return undefined;
    const preventUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', preventUnload);
    return () => window.removeEventListener('beforeunload', preventUnload);
  }, [busy, dirty]);

  const selectImage = useCallback((imageId) => {
    if (busy) return;
    if (String(imageId) === String(selectedImageId)) return;
    setSelectedImageId(imageId);
    setMessage('');
  }, [busy, selectedImageId]);

  const cancelChanges = () => {
    if (busy) return;
    setDisplayTemplate(baselineTemplate);
    setMessage('已取消未保存修改');
    setMessageTone('success');
  };

  const toggleImageGroup = useCallback((groupId, open) => {
    if (busy) return;
    setCollapsedImageGroupIds((current) => setBindingGroupOpen(current, groupId, open));
  }, [busy]);

  const toggleTargetGroup = useCallback((groupId, open) => {
    if (busy) return;
    setCollapsedTargetGroupIds((current) => setBindingGroupOpen(current, groupId, open));
  }, [busy]);

  const toggleTarget = useCallback((slot) => {
    if (busy) return;
    setDisplayTemplate((current) => updateBindingDraft(
      current,
      selectedImageId,
      toggleBindingTarget(getBindingDraft(
        getJianyingBindingImageSlots(current).find(
          (image) => String(image.id) === String(selectedImageId),
        ),
        current,
      ), slot),
    ));
    setMessage('');
  }, [busy, selectedImageId]);

  const quickBind = () => {
    if (busy) return;
    setDisplayTemplate((current) => applyQuickBindings(current));
    setMessage('已按图片时间范围生成绑定草稿，请检查后保存。');
    setMessageTone('success');
  };

  const saveBindings = async () => {
    if (busy) return;
    if (!dirty) return;
    const savingTemplate = displayTemplate;
    setBusy(true);
    setMessage('');
    try {
      await replaceJianyingTemplateSlotBindings(
        buildBindingBatchPayload(savingTemplate),
      );
      setBaselineTemplate(savingTemplate);
      try {
        const refreshed = await onChanged();
        appliedPropRef.current = refreshed;
        setBaselineTemplate(refreshed);
        setDisplayTemplate(refreshed);
        setMessage('槽位绑定已保存');
      } catch {
        setMessage('槽位绑定已保存，但页面刷新失败；重新进入此模板可查看最新结果。');
      }
      setMessageTone('success');
    } catch (error) {
      setMessage(getErrorMessage(error, '保存槽位绑定失败'));
      setMessageTone('danger');
    } finally {
      setBusy(false);
    }
  };

  if (!imageSlots.length) {
    return (
      <div className="jianying-binding-empty">
        <i className="bi bi-image" aria-hidden="true" />
        <strong>模板中没有可替换图片槽位</strong>
        <span>将图片槽位设为可替换后即可配置绑定。</span>
      </div>
    );
  }

  const targetCount = countSlots(targetSections);
  const selectedGroup = selectedImage?.group_id
    ? groupsById.get(String(selectedImage.group_id))
    : null;

  return (
    <div className="jianying-binding-editor">
      <aside className="jianying-binding-images" aria-label="图片槽位">
        <header className="jianying-binding-pane-head">
          <span><strong>图片槽位</strong><small>按时间线顺序选择</small></span>
          <em>{imageSlots.length}</em>
        </header>
        <div className="jianying-binding-images__list">
          {imageSections.map((node) => (
            node.type === 'ungrouped' ? (
              <section className="jianying-binding-ungrouped" key={node.key}>
                <header><strong>未分组</strong><small>{node.children.length} 个图片槽位</small></header>
                <div>
                  {node.children.map(({ key, slot: image }) => (
                    <BindingImage
                      active={String(image.id) === String(selectedImageId)}
                      boundSlots={getBoundSlotsForImage(displayTemplate, image)}
                      busy={busy}
                      image={image}
                      key={key}
                      onSelect={selectImage}
                      slotPaths={slotPaths}
                    />
                  ))}
                </div>
              </section>
            ) : (
              <BindingGroup
                busy={busy}
                collapsedGroupIds={collapsedImageGroupIds}
                countLabel="个图片槽位"
                key={node.key}
                node={node}
                onGroupToggle={toggleImageGroup}
                renderSlot={(image) => (
                  <BindingImage
                    active={String(image.id) === String(selectedImageId)}
                    boundSlots={getBoundSlotsForImage(displayTemplate, image)}
                    busy={busy}
                    image={image}
                    onSelect={selectImage}
                    slotPaths={slotPaths}
                  />
                )}
                searchActive={false}
              />
            )
          ))}
        </div>
      </aside>

      <section className="jianying-binding-targets">
        <header className="jianying-binding-targets__head">
          <div className="jianying-binding-current">
            <span className="jianying-binding-current__icon"><i className="bi bi-image" aria-hidden="true" /></span>
            <span>
              <small>{selectedGroup?.name || '未分组图片'} · 图片槽位只读</small>
              <strong>{selectedImage?.name || '未命名图片槽位'}</strong>
            </span>
          </div>
          <Form.Control
            aria-label="搜索可绑定槽位"
            type="search"
            placeholder="搜索名称、内容或定位 Key"
            value={search}
            disabled={busy}
            onChange={(event) => {
              if (busy) return;
              setSearch(event.target.value);
            }}
          />
        </header>

        {message ? (
          <p className={`jianying-binding-notice is-${messageTone}`} role="alert" aria-live="polite">
            {message}
          </p>
        ) : null}

        <div className="jianying-binding-targets__body">
          {targetSections.map((node) => (
            node.type === 'ungrouped' ? (
              <section className="jianying-binding-ungrouped" key={node.key}>
                <header><strong>未分组</strong><small>{node.children.length} 个可绑定槽位</small></header>
                <div>
                  {node.children.map((child) => (
                    <BindingTarget
                      busy={busy}
                      draftIds={draftIds}
                      key={child.key}
                      onToggle={toggleTarget}
                      owner={owners.get(String(child.slot.id))}
                      ownerPath={slotPaths.get(String(owners.get(String(child.slot.id))?.id))}
                      selectedImageId={selectedImageId}
                      slot={child.slot}
                    />
                  ))}
                </div>
              </section>
            ) : (
              <BindingGroup
                busy={busy}
                collapsedGroupIds={collapsedTargetGroupIds}
                countLabel="个可绑定槽位"
                key={node.key}
                node={node}
                onGroupToggle={toggleTargetGroup}
                renderSlot={(slot) => (
                  <BindingTarget
                    busy={busy}
                    draftIds={draftIds}
                    onToggle={toggleTarget}
                    owner={owners.get(String(slot.id))}
                    ownerPath={slotPaths.get(String(owners.get(String(slot.id))?.id))}
                    selectedImageId={selectedImageId}
                    slot={slot}
                  />
                )}
                searchActive={Boolean(search.trim())}
              />
            )
          ))}
          {!targetCount ? (
            <div className="jianying-binding-targets__empty">
              <strong>{search.trim() ? '没有匹配的槽位' : '没有可绑定槽位'}</strong>
              <span>{search.trim() ? '换一个名称、内容或定位 Key 试试。' : '模板需要包含可替换的文字或音频槽位。'}</span>
            </div>
          ) : null}
        </div>

        <footer className="jianying-binding-actions">
          <span>
            已选择 {draft.text_slot_ids.length} 个文字槽位、{draft.audio_slot_ids.length} 个音频槽位
          </span>
          <div>
            <Button variant="outline-primary" disabled={busy} onClick={quickBind}>
              快速绑定
            </Button>
            <Button variant="outline-secondary" disabled={busy || !dirty} onClick={cancelChanges}>
              取消修改
            </Button>
            <Button disabled={busy || !dirty} onClick={saveBindings}>
              {busy ? <><Spinner size="sm" /> 保存中</> : '保存绑定'}
            </Button>
          </div>
        </footer>
      </section>

    </div>
  );
};

export default JianyingTemplateSlotBindingEditor;
